import { z } from "zod";
import { ER_STAKEHOLDER_TYPES } from "./model";
import { isLegacyPracticalPhone, LEGACY_PHONE_INVALID_MESSAGE } from "./phoneLegacy";
import { requiresCommunicationsRoleJobTitle } from "./roleMap";

const YES_NO_VALUES = ["Yes", "No"] as const;

const registrationAccountCoreSchema = z.object({
  stakeholderType: z.string().trim().min(1, "Stakeholder type is required"),
  jobTitle: z.string().trim().optional().default(""),
  isUnaffiliatedProfessionalErTrainer: z.string().trim().optional().default(""),
  communicationsRole: z.string().trim().optional().default(""),
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  phone: z.string().trim().min(1, "Phone is required"),
  email: z.email("Valid email is required"),
});

function refineRegistrationAccountCore(
  v: z.infer<typeof registrationAccountCoreSchema>,
  ctx: z.RefinementCtx,
): void {
  if (ER_STAKEHOLDER_TYPES.includes(v.stakeholderType as (typeof ER_STAKEHOLDER_TYPES)[number])) {
    if (!v.jobTitle.trim()) {
      ctx.addIssue({
        path: ["jobTitle"],
        code: "custom",
        message: "Job Title is required for Emergency Responder registrations",
      });
    }
    if (
      !YES_NO_VALUES.includes(
        v.isUnaffiliatedProfessionalErTrainer as (typeof YES_NO_VALUES)[number],
      )
    ) {
      ctx.addIssue({
        path: ["isUnaffiliatedProfessionalErTrainer"],
        code: "custom",
        message: "Please select Yes or No",
      });
    }
  }

  if (requiresCommunicationsRoleJobTitle(v.jobTitle) && !v.communicationsRole.trim()) {
    ctx.addIssue({
      path: ["communicationsRole"],
      code: "custom",
      message: "Communications Role is required for this job title",
    });
  }

  if (!isLegacyPracticalPhone(v.phone)) {
    ctx.addIssue({
      path: ["phone"],
      code: "custom",
      message: LEGACY_PHONE_INVALID_MESSAGE,
    });
  }
}

/**
 * Quick Code path when an ABP user already exists but CRM profile was never completed.
 * Password was set at account creation; registration UI omits password fields.
 */
export const registrationAccountResumeSchema = registrationAccountCoreSchema.superRefine((v, ctx) =>
  refineRegistrationAccountCore(v, ctx),
);

export type RegistrationAccountResumeValues = z.infer<typeof registrationAccountResumeSchema>;

export const registrationAccountSchema = registrationAccountCoreSchema
  .extend({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password must be 128 characters or fewer"),
    passwordConfirm: z.string().min(1, "Please confirm your password"),
  })
  .superRefine((v, ctx) => {
    refineRegistrationAccountCore(v, ctx);

    if (v.password.length >= 8) {
      if (
        !/[A-Z]/.test(v.password) ||
        !/[^A-Za-z0-9]/.test(v.password) ||
        !/[0-9]/.test(v.password)
      ) {
        ctx.addIssue({
          path: ["password"],
          code: "custom",
          message:
            "Use at least 8 characters, including one uppercase letter, one number, and one special character (such as !, @, or #).",
        });
      }
    }

    if (v.password !== v.passwordConfirm) {
      ctx.addIssue({
        path: ["passwordConfirm"],
        code: "custom",
        message: "Password does not match",
      });
    }
  });

export type RegistrationAccountValues = z.infer<typeof registrationAccountSchema>;

export const registrationQuickCodeSchema = z.object({
  hasQuickCode: z.boolean(),
  quickCode: z.string().trim().optional().default(""),
});

export type RegistrationQuickCodeValues = z.infer<typeof registrationQuickCodeSchema>;

export type RegisterBuxusRequest = {
  name: string;
  surname: string;
  emailAddress: string;
  password: string;
  username: string;
  phone: string;
};

export function toRegisterBuxusRequest(values: RegistrationAccountValues): RegisterBuxusRequest {
  return {
    name: values.firstName.trim(),
    surname: values.lastName.trim(),
    emailAddress: values.email.trim(),
    password: values.password,
    username: values.email.trim(),
    phone: values.phone.trim(),
  };
}
